import { camelCase, snakeCase, debounce } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Grid, Icon, IconButton, TableCell, TableRow, TextField } from '@material-ui/core';
import { RelationshipType } from '@sierralabs/entity-core';
import EntityPicker from '../pickers/EntityPicker';
import EntityColumnPicker from '../pickers/EntityColumnPicker';
import RelationshipTypePicker from '../pickers/RelationshipTypePicker';
import { useEntityContext } from '../EntityContext';
import EntityRelationshipPicker from '../pickers/EntityRelationshipPicker';
const StyledTableRow = styled(TableRow) `
  vertical-align: top;
  .MuiTableCell-root {
    padding: 12px 8px 4px 8px;
  }
  .picker-cell {
    min-width: 180px;
  }
  .delete-cell {
    padding-top: 18px;
  }
`;
const EntityRelationshipListRow = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const { index, entity, disabled, relation: initialRelation } = props;
    const { entities, trackChanges, validationError, validateSchema } = useEntityContext();
    const [relation, setRelation] = useState(initialRelation);
    const inverseEntity = useMemo(() => relation.target
        ? entities === null || entities === void 0 ? void 0 : entities.find(e => e.name === relation.target)
        : undefined, [relation.target, entities]);
    const isManyToMany = relation.type === RelationshipType.ManyToMany;
    const joinTableOptions = useMemo(() => {
        // based on entity.service.ts getSystemEntitySchemas(), joinTable uses multiple columns
        return relation.joinTable && typeof relation.joinTable === 'object'
            ? relation.joinTable
            : undefined;
    }, [relation]);
    const joinTableEntity = useMemo(() => {
        return (joinTableOptions &&
            (entities === null || entities === void 0 ? void 0 : entities.find(entity => {
                return [entity.id, entity.name, entity.tableName].includes(joinTableOptions.name);
            })));
    }, [entities, joinTableOptions]);
    const handleChange = (updatedRelation) => {
        const newRelation = Object.assign(Object.assign({}, relation), updatedRelation);
        setRelation(newRelation);
        debounce(() => {
            const entityCopy = Object.assign(Object.assign({}, trackChanges.final), { relations: [...(trackChanges.final.relations || [])] });
            entityCopy.relations[index] = newRelation;
            if (validateSchema(entityCopy)) {
                // only update relationship on server side if schema is valid
                trackChanges.update(`relations[${index}]`, newRelation);
            }
        }, 300)();
    };
    const error = validationError.relations[index];
    return (React.createElement(StyledTableRow, null,
        React.createElement(TableCell, null,
            React.createElement(TextField, { fullWidth: true, size: 'small', variant: 'outlined', placeholder: 'Relationship Property Name', disabled: disabled, value: relation.displayName || '', error: !!((_a = error === null || error === void 0 ? void 0 : error.displayName) === null || _a === void 0 ? void 0 : _a.message), helperText: ((_b = error === null || error === void 0 ? void 0 : error.displayName) === null || _b === void 0 ? void 0 : _b.message) ||
                    `Property Key: ${camelCase(relation.displayName)}`, inputProps: { maxLength: 24 }, onChange: event => {
                    handleChange({ displayName: event.target.value });
                } })),
        React.createElement(TableCell, null,
            React.createElement(RelationshipTypePicker, { disabled: disabled, value: relation.type, onChange: type => handleChange({ type }) })),
        React.createElement(TableCell, { className: 'picker-cell' },
            React.createElement(Grid, { container: true, spacing: 2 },
                React.createElement(Grid, { item: true, xs: 12, md: isManyToMany ? 6 : 12 },
                    React.createElement(EntityPicker, { value: relation === null || relation === void 0 ? void 0 : relation.target, disabled: disabled, filter: e => {
                            var _a;
                            return relation.type === RelationshipType.OneToMany
                                ? Boolean((_a = e.relations) === null || _a === void 0 ? void 0 : _a.some(r => r.target === entity.name))
                                : true;
                        }, error: !!((_c = error === null || error === void 0 ? void 0 : error.target) === null || _c === void 0 ? void 0 : _c.message), helperText: !((_d = error === null || error === void 0 ? void 0 : error.target) === null || _d === void 0 ? void 0 : _d.message)
                            ? undefined
                            : relation.type === RelationshipType.OneToMany
                                ? 'Make sure to create the Many-to-One relationship first.'
                                : relation.type === RelationshipType.ManyToMany
                                    ? 'Make sure to create a Junction table first.'
                                    : undefined, onChange: entity => handleChange({ target: entity === null || entity === void 0 ? void 0 : entity.name }) })),
                isManyToMany && (React.createElement(Grid, { item: true, xs: 12, md: 6 },
                    React.createElement(EntityPicker, { disabled: disabled, value: joinTableOptions === null || joinTableOptions === void 0 ? void 0 : joinTableOptions.name, placeholder: 'Select Junction Table', error: !!((_e = error === null || error === void 0 ? void 0 : error.joinTable) === null || _e === void 0 ? void 0 : _e.message), helperText: (_f = error === null || error === void 0 ? void 0 : error.joinTable) === null || _f === void 0 ? void 0 : _f.message, filter: entity => entity.type === 'junction', onChange: newEntity => {
                            // if joinColumn is not in entity.columns, remove it
                            const { columns } = newEntity || entity;
                            const joinColumnName = relation.joinColumn &&
                                typeof relation.joinColumn === 'object'
                                ? relation.joinColumn.name
                                : undefined;
                            const shouldRemove = !columns.some(column => {
                                const name = column.columnName || snakeCase(column.displayName);
                                return (typeof relation.joinColumn === 'object' &&
                                    name === relation.joinColumn.name);
                            });
                            const joinTable = newEntity
                                ? {
                                    name: newEntity.tableName
                                }
                                : undefined;
                            const update = { joinTable };
                            // set joinTable.joinColumns value
                            if (shouldRemove) {
                                update.joinColumn = undefined;
                            }
                            else if (joinTable && joinColumnName) {
                                joinTable.joinColumns = [{ name: joinColumnName }];
                            }
                            handleChange(update);
                        } }))))),
        React.createElement(TableCell, { className: 'picker-cell' }, relation.type === RelationshipType.ManyToOne ||
            relation.type === RelationshipType.OneToOne ? (React.createElement(EntityColumnPicker, { entity: entity, disabled: disabled, value: typeof relation.joinColumn === 'object'
                ? relation.joinColumn.name
                : undefined, error: !!((_g = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _g === void 0 ? void 0 : _g.message), helperText: ((_h = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _h === void 0 ? void 0 : _h.message) ||
                (!relation.joinColumn
                    ? `Recommend using: ${relation.displayName} ID`
                    : undefined), filter: column => (column.type === 'integer' || column.type === 'int') &&
                !column.primary, onChange: (value) => {
                const name = value
                    ? value.columnName || snakeCase(value.displayName)
                    : undefined;
                handleChange({ joinColumn: { name } });
            }, onAdd: displayName => {
                const columnName = snakeCase(displayName);
                trackChanges.insert('columns', {
                    displayName,
                    columnName,
                    type: 'integer'
                });
                handleChange({ joinColumn: { name: columnName } });
            } })) : isManyToMany ? (React.createElement(Grid, { container: true, spacing: 2 },
            React.createElement(Grid, { item: true, xs: 12, md: 6 },
                React.createElement(EntityColumnPicker, { entity: joinTableEntity, disabled: disabled, value: typeof relation.joinColumn === 'object'
                        ? relation.joinColumn.name
                        : undefined, error: !!((_j = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _j === void 0 ? void 0 : _j.message), helperText: (_k = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _k === void 0 ? void 0 : _k.message, filter: column => {
                        return column.type === 'integer' || column.type === 'int';
                    }, onChange: (value) => {
                        const name = value
                            ? value.columnName || snakeCase(value.displayName)
                            : undefined;
                        // set joinColumn and joinTable.joinColumns
                        const joinTable = joinTableOptions || {};
                        joinTable.joinColumns = name ? [{ name }] : undefined;
                        handleChange({
                            joinTable,
                            joinColumn: name ? { name } : undefined
                        });
                    } })),
            React.createElement(Grid, { item: true, xs: 12, md: 6 },
                React.createElement(EntityColumnPicker, { disabled: disabled, entity: joinTableEntity, placeholder: 'Select Inverse Column', value: (_m = (_l = joinTableOptions === null || joinTableOptions === void 0 ? void 0 : joinTableOptions.inverseJoinColumns) === null || _l === void 0 ? void 0 : _l[0]) === null || _m === void 0 ? void 0 : _m.name, error: !!((_o = error === null || error === void 0 ? void 0 : error.inverseJoinColumn) === null || _o === void 0 ? void 0 : _o.message), helperText: (_p = error === null || error === void 0 ? void 0 : error.inverseJoinColumn) === null || _p === void 0 ? void 0 : _p.message, filter: column => {
                        return column.type === 'integer' || column.type === 'int';
                    }, onChange: (value) => {
                        const name = value
                            ? value.columnName || snakeCase(value.displayName)
                            : undefined;
                        const joinTable = joinTableOptions || {};
                        joinTable.inverseJoinColumns = name
                            ? [{ name }]
                            : undefined;
                        handleChange({ joinTable });
                    } })))) : (React.createElement(EntityRelationshipPicker, { disabled: disabled, entity: inverseEntity, value: (_q = inverseEntity === null || inverseEntity === void 0 ? void 0 : inverseEntity.relations) === null || _q === void 0 ? void 0 : _q.find(r => r.propertyName === relation.inverseSide), error: !!((_r = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _r === void 0 ? void 0 : _r.message), helperText: ((_s = error === null || error === void 0 ? void 0 : error.joinColumn) === null || _s === void 0 ? void 0 : _s.message) ||
                `Inverse ${relation.target} Column`, onChange: relation => handleChange({ inverseSide: relation.propertyName }) }))),
        React.createElement(TableCell, { className: 'delete-cell' },
            React.createElement(IconButton, { size: 'small', disabled: disabled, onClick: () => trackChanges.delete(`relations[${index}]`) },
                React.createElement(Icon, null, "delete_outlined")))));
};
export default EntityRelationshipListRow;
//# sourceMappingURL=EntityRelationshipListRow.js.map